// PDF/ICS download provider — v70 simplification.
//
// Design (per user's spec on the export flow):
//   1. Tap → toast "Generating PDF…" (already loading-toast-driven).
//   2. Fetch the file.
//   3. **Desktop / non-share browsers:** programmatically click a hidden
//      anchor to trigger the browser's native download immediately.
//      No modal. No preview. Success toast confirms.
//   4. **Mobile browsers with Web Share API:** show a single, minimal
//      "Save your PDF" sheet with one primary "Save / Share" button.
//      Browser sandbox (e.g. embedded iframe previews on Android Chrome)
//      block direct anchor downloads, so we need a user-gesture-driven
//      `navigator.share({ files: [...] })` call. The sheet IS the
//      gesture target — no preview, no extra options.
//
// What changed from v60:
//   - Inline PDF preview pane removed entirely. The user's testing was
//     clear: "Remove the PDF preview window from the export flow.
//     No preview modal before download."
//   - Desktop path now auto-downloads — the dialog never appears.
//   - Mobile path keeps a one-button dialog (necessary for the share
//     gesture; everything else is gone).
//   - "Open in new tab" / "fallback link" / size-display all removed —
//     they were preview-page chrome, not part of the core save flow.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, FileText, Share2, CalendarDays } from "lucide-react";
import { API, api, getToken } from "@/lib/api";
import { _registerDownloadImpl } from "@/lib/downloadFile";

const PdfDownloadContext = createContext(null);

// Hybrid fallback tuning (iter 124.pdf-hybrid, per user spec).
// After 15s: offer the "Email it to me instead" button + reassuring
// progress copy. After 30s of total elapsed sync-wait: auto-queue the
// background export if the caller supplied `asyncFallback`. Numbers
// are deliberate — 15s is roughly the P99 of a healthy render on a
// stable connection, 30s is where mobile carriers start dropping the
// idle upstream on flaky links.
const OFFER_FALLBACK_MS = 15_000;
const AUTO_FALLBACK_MS = 30_000;
// Absolute cap on the sync attempt even without asyncFallback support.
// Prevents the fetch hanging forever on truly dead connections.
const HARD_TIMEOUT_MS = 120_000;

const FILE_KINDS = {
  pdf: {
    mime: "application/pdf",
    label: "PDF",
    icon: FileText,
    magicCheck: true,
    successToast: "Saved",
    loadingLabel: "Generating PDF",
    sheetTitle: "Save your PDF",
    sheetCta: "Save / Share",
  },
  ics: {
    mime: "text/calendar",
    label: "Calendar file",
    icon: CalendarDays,
    magicCheck: false,
    successToast: "Calendar file ready",
    loadingLabel: "Generating calendar file",
    sheetTitle: "Add to calendar",
    sheetCta: "Add to calendar",
  },
};

// True when the browser supports navigator.share with files. iOS 15+,
// Android Chrome 89+, plus most modern mobile browsers. Used to pick
// between the auto-download path and the one-button share sheet path.
function _canShareFiles(file) {
  return typeof navigator !== "undefined"
    && typeof navigator.share === "function"
    && typeof navigator.canShare === "function"
    && navigator.canShare({ files: [file] });
}

// Programmatic anchor click. Returns true if the browser appears to
// have honoured the click (we never actually know for sure — the
// download API is fire-and-forget — but if `dispatchEvent` returned
// `true` the gesture wasn't cancelled).
function _autoDownload(blob, filename) {
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    // Some Safari versions need the anchor in the DOM for the click to
    // count as a user-initiated download.
    a.style.display = "none";
    document.body.appendChild(a);
    const ok = a.dispatchEvent(new MouseEvent("click", {
      bubbles: true, cancelable: true, view: window,
    }));
    // Clean up shortly after — any download negotiation has already
    // happened by then.
    setTimeout(() => {
      try { a.remove(); } catch (e) { /* noop */ void e; }
      try { URL.revokeObjectURL(url); } catch (e) { /* noop */ void e; }
    }, 1000);
    return ok;
  } catch (err) {
    console.warn("[file] auto-download failed:", err);
    return false;
  }
}

export function PdfDownloadProvider({ children }) {
  const [pending, setPending] = useState(null); // null | { blob, filename, kind, canShare }
  // Single in-flight tracker so a quick double-tap doesn't fire two
  // simultaneous fetches against the same export endpoint.
  const inFlight = useRef(false);

  // Iter 124.pdf-hybrid — Hybrid fallback flow (per user spec).
  //
  //   0s    kick off the sync fetch (`GET .../export/pdf?...`).
  //         Toast: "Generating PDF…"
  //   15s   if the fetch hasn't resolved, upgrade the toast to
  //         "This is taking longer than expected" + an "Email it to
  //         me instead" action button. The sync fetch KEEPS RUNNING —
  //         we don't auto-enqueue the background job yet.
  //   30s   if it still hasn't resolved, auto-queue the background
  //         export (if `asyncFallback` was supplied), abort the sync
  //         fetch, and confirm "You'll receive an email when it's
  //         ready." Guarantees the user is never left staring at a
  //         spinner.
  //   Any time — if the sync fetch resolves first, cancel timers,
  //   deliver the blob, and (if a background job was manually
  //   requested via the button) do NOT enqueue it — the user
  //   already got their file.
  //
  // If `opts.asyncFallback` is omitted the flow degrades gracefully:
  // the 15s toast upgrade + 30s auto-fallback don't fire. The sync
  // fetch still has HARD_TIMEOUT_MS as its ceiling.
  const _fetchOnce = useCallback(async (url, timeoutMs, signalRef) => {
    const ac = new AbortController();
    signalRef.current = ac;
    const timeoutId = setTimeout(() => ac.abort(), timeoutMs);
    try {
      const res = await fetch(url, {
        credentials: "include",
        headers: { Authorization: `Bearer ${getToken()}` },
        signal: ac.signal,
      });
      return res;
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  // Enqueue the background export via the caller-supplied endpoint.
  // Returns { ok: true } on 202/200, or { ok: false, error } on
  // failure. The response payload is discarded — the completion email
  // is the deliverable, not the API response body.
  //
  // v77.99.140 (iter 124.exports-inbox-prod-hotfix) — Uses the shared
  // `api` axios instance so the request interceptor injects the
  // `X-CSRF-Token` header from the `cpc_csrf` cookie automatically.
  // Previously used raw `fetch` which sent an empty `Authorization:
  // Bearer ` header (getToken() returns "" for cookie-only sessions),
  // failing the middleware's Bearer short-circuit and then failing CSRF
  // (no header echoed) — resulting in 403 for every production user.
  const _queueBackgroundExport = useCallback(async (asyncFallback) => {
    try {
      const endpoint = asyncFallback.endpoint || "";
      // Strip a leading /api if the caller included one; `api` already
      // has the /api prefix in its baseURL.
      const path = endpoint.startsWith("/api/")
        ? endpoint.slice(4)
        : endpoint;
      const res = await api.post(path, asyncFallback.payload || {});
      const j = res.data || {};
      return { ok: true, jobId: j.job_id, emailTo: j.email_to };
    } catch (err) {
      let detail = err?.message || "Network error";
      const respData = err?.response?.data;
      if (respData) {
        if (typeof respData.detail === "object" && respData.detail !== null) {
          detail = respData.detail.message || respData.detail.detail || JSON.stringify(respData.detail);
        } else if (typeof respData.detail === "string") {
          detail = respData.detail;
        } else if (err.response?.status) {
          detail = `Server returned ${err.response.status}`;
        }
      }
      return { ok: false, error: detail };
    }
  }, []);

  const _renderSupportAction = useCallback((url, errMsg, filename) => {
    // Sonner supports one action button per toast; we bundle
    // "Copy diagnostic + open support email" so the user can send us
    // exactly what we need in a single tap.
    return {
      label: "Contact support",
      onClick: () => {
        const diag = [
          `File: ${filename}`,
          `URL: ${url}`,
          `Error: ${errMsg || "unknown"}`,
          `User-agent: ${navigator.userAgent}`,
          `When: ${new Date().toISOString()}`,
        ].join("\n");
        try { navigator.clipboard && navigator.clipboard.writeText(diag); } catch (_) { /* no-op */ }
        const subject = encodeURIComponent("CoParent Console — PDF export failed");
        const body = encodeURIComponent(
          "Hi CoParent Console support team,\n\n" +
          "The PDF export failed for me. Diagnostic details below (also copied to your clipboard).\n\n" +
          diag + "\n\nThank you.",
        );
        window.location.href = `mailto:support@coparentconsole.com?subject=${subject}&body=${body}`;
      },
    };
  }, []);

  const downloadFile = useCallback(async (url, filename, opts = {}) => {
    if (inFlight.current) return false;
    const kind = FILE_KINDS[opts.kind] ? opts.kind : "pdf";
    const conf = FILE_KINDS[kind];
    const { label = conf.loadingLabel, asyncFallback = null } = opts;
    inFlight.current = true;
    const t = toast.loading(`${label}…`);
    const acRef = { current: null };

    // Timer + state coordination for the hybrid fallback. Shared via
    // closures so the fetch handler, the button callback, and the
    // auto-timeout can all reach them without a ref dance.
    const state = {
      settled: false,     // sync fetch has delivered a result (success OR permanent failure)
      fallbackQueued: false, // background job already POSTed
      offerTimer: null,   // 15s handle — swap toast to include "Email it to me"
      autoTimer: null,    // 30s handle — auto-queue background
    };
    const _clearTimers = () => {
      if (state.offerTimer) { clearTimeout(state.offerTimer); state.offerTimer = null; }
      if (state.autoTimer) { clearTimeout(state.autoTimer); state.autoTimer = null; }
    };

    // Enqueue-and-notify — shared by the manual button + the 30s
    // auto-fallback. Idempotent via `state.fallbackQueued`.
    const _triggerFallback = async ({ auto }) => {
      if (state.fallbackQueued || state.settled || !asyncFallback) return;
      state.fallbackQueued = true;
      _clearTimers();
      // Abort the still-running sync fetch — the background job will
      // produce the deliverable, and keeping the sync HTTP call open
      // wastes bandwidth on a mobile connection.
      try { if (acRef.current) acRef.current.abort(); } catch (_) { /* noop */ }
      toast.loading("Queueing your export…", { id: t });
      const r = await _queueBackgroundExport(asyncFallback);
      if (r.ok) {
        const emailLine = r.emailTo
          ? `We'll email it to ${r.emailTo} when it's ready.`
          : "We'll email it to you when it's ready.";
        const headline = auto
          ? "Still going — moved to email delivery"
          : "Got it — we'll email it to you";
        toast.success(`${headline}. ${emailLine} You can close this page.`, {
          id: t,
          duration: 10000,
          // Iter 124.exports-inbox — surface the Exports page as the
          // shortest path back to the file, in case the email lands
          // in spam / the user closes the tab / the user wants to
          // check progress before the email arrives.
          action: {
            label: "View exports",
            onClick: () => { window.location.href = "/app/exports"; },
          },
        });
      } else {
        toast.error(
          `Couldn't queue the email fallback: ${r.error || "unknown error"}. Please try again.`,
          {
            id: t,
            duration: Infinity,
            action: {
              label: "Retry",
              onClick: () => { downloadFile(url, filename, opts); },
            },
          },
        );
      }
    };

    // Run one attempt end-to-end and return either { blob } on success
    // or { errorMsg, aborted } on failure.
    const _attempt = async (timeoutMs) => {
      try {
        console.info("[file] fetch start", { url, filename, kind, timeoutMs });
        const res = await _fetchOnce(url, timeoutMs, acRef);
        if (!res.ok) {
          let detail = `Server returned ${res.status}`;
          try {
            const ct = res.headers.get("content-type") || "";
            if (ct.includes("application/json")) {
              const j = await res.json();
              if (j && typeof j.detail === "object" && j.detail !== null) {
                detail = j.detail.message || j.detail.detail || JSON.stringify(j.detail);
              } else if (typeof j.detail === "string") {
                detail = j.detail;
              }
            }
          } catch (parseErr) {
            console.warn("[file] error parse failed:", parseErr);
          }
          return { errorMsg: detail, aborted: false, permanent: res.status === 402 || res.status === 401 || res.status === 403 };
        }
        const rawBlob = await res.blob();
        if (!rawBlob || rawBlob.size === 0) return { errorMsg: "Empty file returned", aborted: false };
        const blob = new Blob([rawBlob], { type: conf.mime });
        if (conf.magicCheck) {
          const headBytes = await blob.slice(0, 4).arrayBuffer();
          const headStr = String.fromCharCode(...new Uint8Array(headBytes));
          if (headStr !== "%PDF") {
            return { errorMsg: `Invalid PDF (file starts with "${headStr}" instead of %PDF)`, aborted: false };
          }
        }
        console.info("[file] blob ready", { filename, size: blob.size, mime: conf.mime, timeoutMs });
        return { blob };
      } catch (err) {
        const aborted = err && (err.name === "AbortError" || (acRef.current && acRef.current.signal.aborted));
        return {
          errorMsg: aborted
            ? "The export took longer than expected"
            : (err && err.message) || "Network error",
          aborted,
        };
      }
    };

    try {
      // Arm the hybrid fallback timers ONLY when the caller supplied
      // async-fallback metadata AND we're doing a PDF (ics stays with
      // the simple flow — calendar files are small, timeouts don't
      // happen in practice).
      if (asyncFallback && kind === "pdf") {
        state.offerTimer = setTimeout(() => {
          if (state.settled || state.fallbackQueued) return;
          toast.loading(
            "This report is taking longer than expected. You can keep waiting or we'll email it to you when it's ready.",
            {
              id: t,
              duration: Infinity,
              action: {
                label: "Email it to me instead",
                onClick: () => { _triggerFallback({ auto: false }); },
              },
            },
          );
        }, OFFER_FALLBACK_MS);
        state.autoTimer = setTimeout(() => {
          if (state.settled || state.fallbackQueued) return;
          _triggerFallback({ auto: true });
        }, AUTO_FALLBACK_MS);
      }

      const result = await _attempt(HARD_TIMEOUT_MS);
      state.settled = true;
      _clearTimers();

      // If the background job was already queued (30s auto-fallback
      // fired first), respect that path — the user has been told to
      // expect an email. Don't also deliver the sync blob (would be
      // duplicate work + confusing).
      if (state.fallbackQueued) {
        return true;
      }

      if (result.blob) {
        _autoDownload(result.blob, filename);
        let canShare = false;
        if (kind !== "ics") {
          try {
            const probe = new File([result.blob], filename, { type: conf.mime });
            canShare = _canShareFiles(probe);
          } catch (e) {
            console.warn("[file] File ctor / canShare probe failed:", e);
          }
        }
        if (!canShare) {
          if (kind === "ics") {
            toast.success("Calendar file downloaded — tap it in your Downloads to add to your calendar.", { id: t, duration: 6000 });
          } else {
            toast.success(conf.successToast, { id: t });
          }
        } else {
          setPending({ blob: result.blob, filename, kind, canShare: true });
          toast.dismiss(t);
        }
        return true;
      }

      // Sync attempt failed permanently (auth/upgrade/forbidden) —
      // don't fall back to email, the background job would hit the
      // same 4xx.
      const errMsg = result.errorMsg || "Couldn't generate the file";
      if (result.permanent) {
        toast.error(errMsg, { id: t, duration: 8000 });
        return false;
      }

      // Non-permanent failure (timeout, network) AND we have a
      // fallback endpoint but hadn't queued yet (e.g. the HARD timeout
      // beat the AUTO timer, or the response body was empty).
      // Auto-queue as a courtesy so the user always gets their file.
      if (asyncFallback && kind === "pdf") {
        await _triggerFallback({ auto: true });
        return true;
      }

      // No fallback available — surface a persistent error toast with
      // retry + support-email actions.
      const humanMsg = `Couldn't generate the file. ${errMsg}. Please try again — narrower date range or a stronger connection often helps.`;
      console.warn(`[file] download failed for ${url}:`, errMsg);
      toast.error(humanMsg, {
        id: t,
        duration: Infinity,
        action: {
          label: "Retry",
          onClick: () => { downloadFile(url, filename, opts); },
        },
        cancel: _renderSupportAction(url, errMsg, filename),
      });
      return false;
    } finally {
      _clearTimers();
      inFlight.current = false;
    }
  }, [_fetchOnce, _queueBackgroundExport, _renderSupportAction]);

  useEffect(() => {
    _registerDownloadImpl(downloadFile);
    return () => _registerDownloadImpl(null);
  }, [downloadFile]);

  const shareFile = async () => {
    if (!pending) return;
    const conf = FILE_KINDS[pending.kind] || FILE_KINDS.pdf;
    try {
      const file = new File([pending.blob], pending.filename, { type: conf.mime });
      await navigator.share({
        files: [file],
        title: pending.filename,
        text: "CoParent Console export",
      });
      // Don't auto-close on success — iOS sometimes resolves the share
      // promise before the sheet actually lands. User taps Done / Close.
      // We do close because most Android browsers resolve only after the
      // sheet has closed. Compromise: close optimistically; users can
      // re-export if needed.
      setPending(null);
      toast.success(conf.successToast);
    } catch (err) {
      // AbortError → user cancelled the share sheet, that's fine.
      if (err && err.name !== "AbortError") {
        // v77.99.39 — Auto-fallback instead of dead-ending. Samsung
        // Internet, older Edge, and some Android WebViews accept the
        // share probe but throw on the actual `share()` call. Rather
        // than blame the device, silently drop the file into the
        // user's Downloads folder and tell them where it went.
        console.warn("[file] share failed, falling back to auto-download:", err);
        try {
          _autoDownload(pending.blob, pending.filename);
          toast.success(
            pending.kind === "ics"
              ? "Saved to your Downloads — tap the file there to add to your calendar."
              : "Saved to your Downloads folder.",
            { duration: 6000 },
          );
          setPending(null);
        } catch (e2) {
          console.warn("[file] auto-download fallback also failed:", e2);
          toast.error("Couldn't save the file. Please try again, or use a different browser.");
        }
      }
    }
  };

  const close = () => {
    // Iter 123.pdf-hotfix — File was already auto-downloaded when the
    // sheet appeared, so dismissing / cancelling requires no fallback
    // work here. Just clear the pending state.
    setPending(null);
  };

  const conf = pending ? (FILE_KINDS[pending.kind] || FILE_KINDS.pdf) : FILE_KINDS.pdf;
  const HeaderIcon = conf.icon;

  // v77.54 — Format the file size into a friendly label so the user can
  // tell at a glance whether they're about to save a 50 KB receipt or
  // a 5 MB court bundle.
  const formatBytes = (n) => {
    if (!n || n <= 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };
  const fileSizeLabel = pending?.blob?.size ? formatBytes(pending.blob.size) : "";

  // Per-kind helper copy. Kept short — the page is mobile-first and
  // any extra wall-of-text pushes the action buttons below the fold.
  // Iter 123.pdf-hotfix — File is already in Downloads by the time
  // this sheet renders, so the copy shifts from "choose how to save"
  // to "optionally share via your device's share sheet".
  const helperCopy = pending?.kind === "ics"
    ? "Your calendar file has been saved to your device. Open it from Downloads to add it to your calendar app, or use the share sheet below."
    : "Your export has been saved to your device's Downloads. You can close this, or use the share sheet to send it to email, cloud storage, WhatsApp, etc.";

  // Primary action label. Wording shifts depending on whether the
  // browser supports the OS share sheet (mobile) or whether we're
  // forced to fall back to a plain save (some old browsers expose
  // navigator.share without file support).
  const primaryActionLabel = pending?.kind === "ics" ? "Add to calendar" : "Save / Share";
  const primaryActionHint = pending?.kind === "ics"
    ? "Opens your device's calendar app"
    : "Opens your share sheet — email, Drive, AirDrop, WhatsApp, etc.";

  return (
    <PdfDownloadContext.Provider value={{ downloadFile }}>
      {children}
      {/* Mobile-only save sheet. Desktop never sees this — the desktop
          path triggers the browser's native download immediately and
          this dialog never opens. */}
      <Dialog open={!!pending} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent
          className="bg-white border border-[#D9E1E3] rounded-md shadow-xl max-w-md p-0 overflow-hidden"
          data-testid="pdf-ready-dialog"
        >
          {/* Branded header strip — soft teal tint matches the rest of
              the app's primary cards. */}
          <DialogHeader className="bg-[#F4FAFA] border-b border-[#D6F0EF] px-5 py-4 space-y-0">
            <DialogTitle className="font-heading text-lg inline-flex items-center gap-2.5 text-[#273131]">
              <span className="w-9 h-9 rounded-sm bg-white border border-[#A7D8DA] inline-flex items-center justify-center shadow-sm">
                <HeaderIcon className="w-5 h-5 text-[#116B6E]" strokeWidth={1.6} />
              </span>
              {conf.sheetTitle}
            </DialogTitle>
          </DialogHeader>

          <div className="px-5 py-5 space-y-5">
            {/* Filename + size chip */}
            <div
              className="flex items-start justify-between gap-3 px-3 py-2.5 border border-[#D9E1E3] bg-[#FAFCFC] rounded-sm"
              data-testid="pdf-ready-meta"
            >
              <div className="min-w-0 flex-1">
                <div
                  className="text-sm font-medium text-[#2E3A3F] truncate font-mono-data"
                  data-testid="pdf-ready-filename"
                  title={pending?.filename || "document"}
                >
                  {pending?.filename || "document"}
                </div>
                <div className="text-[11px] uppercase tracking-[0.08em] text-[#6F828A] mt-0.5">
                  {conf.label}{fileSizeLabel ? ` · ${fileSizeLabel}` : ""}
                </div>
              </div>
            </div>

            {/* Helper copy */}
            <p className="text-sm text-[#3F595B] leading-relaxed" data-testid="pdf-ready-helper">
              {helperCopy}
            </p>

            {/* Primary action — Save / Share */}
            <div className="space-y-1.5">
              <Button
                onClick={shareFile}
                className="w-full bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-sm h-11"
                data-testid="pdf-ready-share"
              >
                <Share2 className="w-4 h-4 mr-2" strokeWidth={1.8} />
                {primaryActionLabel}
              </Button>
              <p className="text-[11px] text-[#6F828A] pl-1">{primaryActionHint}</p>
            </div>

            {/* Secondary action — Save an additional copy on this device */}
            <div className="space-y-1.5">
              <Button
                type="button"
                onClick={() => { _autoDownload(pending.blob, pending.filename); toast.success("Saved another copy to your Downloads."); }}
                variant="outline"
                className="w-full rounded-sm h-11 border-[#A7D8DA] text-[#116B6E] hover:bg-[#E0F2F2]"
                data-testid="pdf-ready-download"
              >
                <Download className="w-4 h-4 mr-2" strokeWidth={1.8} />
                Save another copy
              </Button>
              <p className="text-[11px] text-[#6F828A] pl-1">
                A copy has already been saved to your Downloads.
              </p>
            </div>

            {/* Cancel — quietly available without tempting an accidental tap */}
            <div className="pt-1 border-t border-[#EEF3F4]" />
            <button
              type="button"
              onClick={() => setPending(null)}
              className="block mx-auto text-xs text-[#6F828A] hover:text-[#2E3A3F] underline-offset-2 hover:underline"
              data-testid="pdf-ready-cancel"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </PdfDownloadContext.Provider>
  );
}

export function usePdfDownload() {
  const ctx = useContext(PdfDownloadContext);
  if (!ctx) throw new Error("usePdfDownload must be used inside <PdfDownloadProvider>");
  return ctx.downloadFile;
}

// Convenience hook for .ics calendar exports — same flow, but the
// caller doesn't have to remember to pass `{ kind: "ics" }` every time.
export function useIcsDownload() {
  const ctx = useContext(PdfDownloadContext);
  if (!ctx) throw new Error("useIcsDownload must be used inside <PdfDownloadProvider>");
  return useCallback(
    (url, filename, opts = {}) => ctx.downloadFile(url, filename, { ...opts, kind: "ics" }),
    [ctx],
  );
}
